import { useState } from "react";
import { Sidebar } from "@/components/Sidebar";
import { MobileHeader, PrimaryNav } from "@/components/TopNav";
import { SubNav } from "@/components/SubNav";
import { ProductRow } from "@/components/ProductRow";
import { DrinkRow } from "@/components/DrinkRow";
import { SmartImage } from "@/components/SmartImage";
import { ProductDetails } from "@/components/ProductDetails";
import { MenuControls } from "@/components/MenuControls";
import { BackToTop } from "@/components/BackToTop";
import { SplashScreen } from "@/components/SplashScreen";
import { Link } from "react-router-dom";
import { useScrollSpy } from "@/hooks/useScrollSpy";
import { matchesSearch } from "@/lib/menuFilters";
import {
  SECTIONS,
  RESTAURANT,
  PRODUCTS,
  productsFor,
  groupProducts,
} from "@/data/menu";

const ANCHOR = "scroll-mt-[160px] lg:scroll-mt-[72px]";

// Chapter kicker (once per major section) — sober, editorial.
const Chapter = ({ label }) => (
  <div className="mb-6 border-t border-roty-line pt-6 lg:mb-8 lg:pt-8">
    <span className="text-[11px] font-extrabold uppercase tracking-[0.34em] text-roty-red">
      {label}
    </span>
  </div>
);

// Subcategory / section title — large, no decorative underline.
const BigHeader = ({ title }) => (
  <h2 className="roty-display mb-6 text-[2.1rem] leading-[0.95] text-roty-ink lg:mb-7 lg:text-[3.2rem]">
    {title}
  </h2>
);

const FoodBlock = ({ sectionId, sub, items, onOpen, priority = false }) => {
  const groups = groupProducts(items);
  return (
    <div id={`sub-${sub.id}`} data-spy={sub.id} data-section={sectionId} className={ANCHOR}>
      <BigHeader title={sub.label} />
      {sub.image ? (
        <div className="mb-7 overflow-hidden rounded-[6px] bg-roty-line/40">
          <div className="aspect-[16/10] w-full sm:aspect-[16/9]">
            <SmartImage
              base={sub.image}
              alt={`ROTY ${sub.label}`}
              width={800}
              height={450}
              widths={[400, 800, 1200]}
              sizes="(max-width: 1023px) calc(100vw - 40px), 720px"
              eager={priority}
              className="h-full w-full object-cover"
            />
          </div>
        </div>
      ) : null}
      <div className="space-y-8">
        {groups.map((g, gi) => (
          <div key={gi}>
            {g.label ? (
              <p className="mb-1 text-[11px] font-extrabold uppercase tracking-[0.22em] text-roty-gold-dark">
                {g.label}
              </p>
            ) : null}
            <div className="border-t border-roty-line">
              {g.items.map((p, i) => (
                <div key={`${p.name}-${i}`} className="border-b border-roty-line">
                  <ProductRow product={p} onOpen={onOpen} />
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const DrinkBlock = ({ sectionId, sub, items }) => {
  const groups = groupProducts(items);
  return (
    <div id={`sub-${sub.id}`} data-spy={sub.id} data-section={sectionId} className={ANCHOR}>
      <BigHeader title={sub.label} />
      <div className="space-y-6">
        {groups.map((g, gi) => (
          <div key={gi}>
            {g.label ? (
              <p className="mb-1 text-[11px] font-extrabold uppercase tracking-[0.22em] text-roty-gold-dark">
                {g.label}
              </p>
            ) : null}
            <ul className="divide-y divide-roty-line border-t border-roty-line">
              {g.items.map((p, i) => (
                <DrinkRow key={`${p.name}-${i}`} product={p} />
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function Menu() {
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [postActive, setPostActive] = useState(false);
  const [showSplash, setShowSplash] = useState(true);
  const activeSpy = useScrollSpy(160, [postActive, query]);

  // Filtrul „DE POST” se aplică EXCLUSIV categoriei MÂNCARE:
  // activ → în MÂNCARE doar produsele postOnly; inactiv → postOnly ascunse.
  // BĂUTURI / DESERT / EXTRA nu sunt filtrate niciodată de „DE POST”.
  const predicate = (p) => {
    if (!matchesSearch(p, query)) return false;
    if (postActive) return p.category !== "mancare" || Boolean(p.postOnly);
    return !p.postOnly;
  };
  const visible = (items) => items.filter(predicate);
  const isFiltering = query.trim() !== "";
  const totalMatches = PRODUCTS.filter(predicate).length;

  const openProduct = (p) => {
    setSelected(p);
    setOpen(true);
  };

  // Derive active top-level section from the active spy target.
  const activeSection =
    SECTIONS.find(
      (s) => s.id === activeSpy || s.subcategories.some((sub) => sub.id === activeSpy)
    )?.id || SECTIONS[0].id;

  const activeSectionObj = SECTIONS.find((s) => s.id === activeSection);
  const subcategories = activeSectionObj?.subcategories || [];

  return (
    <div className="min-h-screen bg-roty-cream">
      {showSplash ? <SplashScreen onDone={() => setShowSplash(false)} /> : null}
      <MobileHeader />
      <PrimaryNav activeSection={activeSection} />

      <div className="lg:flex">
        <Sidebar activeSection={activeSection} />

        <main className="min-w-0 flex-1 roty-fade">
          <div className="mx-auto max-w-[820px] px-5 pb-28 lg:px-16">
            <h1 className="sr-only">ROTY FOOD — Meniu</h1>
            <SubNav subcategories={subcategories} activeSub={activeSpy} />

            <MenuControls query={query} setQuery={setQuery} postActive={postActive} setPostActive={setPostActive} />

            {isFiltering && totalMatches === 0 ? (
              <div data-testid="menu-empty" className="py-24 text-center">
                <p className="roty-display text-2xl text-roty-red">Niciun rezultat</p>
                <p className="mt-2 text-sm text-roty-ash">
                  Nu am găsit produse pentru căutarea ta.
                </p>
              </div>
            ) : null}

            {/* MÂNCARE & BĂUTURI — secțiuni cu subcategorii */}
            {SECTIONS.filter((s) => s.subcategories.length > 0).map((section, sectionIdx) => {
              const subBlocks = section.subcategories
                .map((sub) => ({ sub, items: visible(productsFor(section.id, sub.id)) }))
                .filter((b) => b.items.length > 0);
              if (!subBlocks.length) return null;
              return (
                <section
                  key={section.id}
                  id={`sec-${section.id}`}
                  className="scroll-mt-[150px] pt-8 lg:scroll-mt-[24px] lg:pt-10"
                >
                  <Chapter label={section.label} />
                  <div className="space-y-14 lg:space-y-20">
                    {subBlocks.map(({ sub, items }, blockIdx) =>
                      section.id === "bauturi" ? (
                        <DrinkBlock key={sub.id} sectionId={section.id} sub={sub} items={items} />
                      ) : (
                        <FoodBlock
                          key={sub.id}
                          sectionId={section.id}
                          sub={sub}
                          items={items}
                          onOpen={openProduct}
                          priority={sectionIdx === 0 && blockIdx === 0}
                        />
                      )
                    )}
                  </div>
                </section>
              );
            })}

            {/* DESERT & EXTRA — secțiuni fără subcategorii */}
            {SECTIONS.filter((s) => s.subcategories.length === 0).map((section) => {
              const items = visible(productsFor(section.id));
              if (!items.length) return null;
              const isDrinkStyle = section.id === "extra";
              return (
                <section
                  key={section.id}
                  id={`sec-${section.id}`}
                  data-spy={section.id}
                  data-section={section.id}
                  className="scroll-mt-[150px] pt-12 lg:scroll-mt-[24px] lg:pt-16"
                >
                  <div className="mb-6 border-t border-roty-line pt-6 lg:mb-8 lg:pt-8" />
                  <BigHeader title={section.label} />
                  {isDrinkStyle ? (
                    <ul className="divide-y divide-roty-line border-t border-roty-line">
                      {items.map((p, i) => (
                        <DrinkRow key={`${p.name}-${i}`} product={p} />
                      ))}
                    </ul>
                  ) : (
                    <div className="border-t border-roty-line">
                      {items.map((p, i) => (
                        <div key={`${p.name}-${i}`} className="border-b border-roty-line">
                          <ProductRow product={p} onOpen={openProduct} />
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              );
            })}

            {/* Footer minimalist */}
            <footer className="mt-20 border-t-2 border-roty-red pt-6 text-sm text-roty-ash">
              <p className="roty-display text-xl text-roty-red">ROTY FOOD</p>
              <p className="mt-1">Pizza &amp; Coffee · Valea Bourei · Suceava</p>
              <a
                href={`tel:${RESTAURANT.phone.replace(/\s/g, "")}`}
                className="mt-1 inline-block font-bold text-roty-ink"
              >
                {RESTAURANT.phone}
              </a>
              <nav
                data-testid="footer-legal"
                className="mt-4 flex flex-wrap gap-x-5 gap-y-2 text-xs font-semibold"
              >
                <Link
                  to="/politica-confidentialitate"
                  className="text-roty-ink transition-colors hover:text-roty-red"
                >
                  Politica de confidențialitate
                </Link>
                <Link
                  to="/politica-cookies"
                  className="text-roty-ink transition-colors hover:text-roty-red"
                >
                  Politica cookies
                </Link>
                <Link
                  to="/termeni-si-conditii"
                  className="text-roty-ink transition-colors hover:text-roty-red"
                >
                  Termeni și condiții
                </Link>
              </nav>
              <p className="mt-4 text-xs text-roty-ash/70">
                © {new Date().getFullYear()} ROTY FOOD. Toate drepturile rezervate.
              </p>
              <p className="mt-1 text-xs text-roty-ash/50">
                Meniu digital realizat de{" "}
                <a
                  href="https://wa.me/40754482899"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-credit-alexandru"
                  className="underline decoration-roty-ash/30 underline-offset-2 transition-colors hover:text-roty-red"
                >
                  Alexandru
                </a>
              </p>
            </footer>
          </div>
        </main>
      </div>

      <ProductDetails product={selected} open={open} onOpenChange={setOpen} />
      <BackToTop />
    </div>
  );
}
